type TraitRow = [number, string, string, string];

interface TraitTable {
  trait: string;
  abbr: string;
  cols: [string, string, string];
  rows: TraitRow[];
}

const traitTables: TraitTable[] = [
  {
    trait: 'AGILITY',
    abbr: 'AGI',
    cols: ['SPRINT', 'LEAP', 'MANEUVER'],
    rows: [
      [5, 'Sprint within Close range across an open field with an enemy present.', 'Make a running jump of half your height (about 3 feet for a human).', 'Walk slowly across a narrow beam.'],
      [10, 'Sprint within Far range across an open field with an enemy present.', 'Make a running jump of your height (about 6 feet for a human).', 'Walk quickly across a narrow beam.'],
      [15, 'Sprint across Close range rough terrain with an enemy present.', 'Make a running jump of double your height (about 12 feet for a human).', 'Run across a narrow beam.'],
      [20, 'Sprint across Close range through an active battle of multiple enemies.', 'Make a running jump of three times your height (about 18 feet for a human).', 'Run across a narrow beam in heavy wind.'],
      [25, 'Sprint within Far range through a pitched battle in rough terrain.', 'Make a running jump of five times your height (about 30 feet for a human).', 'Run across a very narrow beam in an active rainstorm.'],
      [30, 'Sprint across the heads of your enemies in a pitched battle.', 'Make a running jump of ten times your height (about 60 feet for a human).', 'Run across an inch-wide, oil-slicked beam in an active rainstorm.'],
    ],
  },
  {
    trait: 'STRENGTH',
    abbr: 'STR',
    cols: ['LIFT', 'SMASH', 'GRAPPLE'],
    rows: [
      [5, 'Lift a chair.', 'Destroy a glass cup.', 'Subdue a child.'],
      [10, 'Lift a table or small chest.', 'Destroy a small wooden table.', 'Subdue a weak adult.'],
      [15, 'Lift a grown person or large chest.', 'Break through a wooden door.', 'Subdue an average adult.'],
      [20, 'Lift the side of a laden cart or carry a large chest up stairs.', 'Break through a stone wall.', 'Subdue a skilled wrestler.'],
      [25, 'Lift a horse, an ox, or a large monster.', "Break through a dragon's teeth.", 'Subdue a large beast.'],
      [30, 'Lift a falling portcullis gate.', "Break a god's grip.", 'Subdue a legendary beast.'],
    ],
  },
  {
    trait: 'FINESSE',
    abbr: 'FIN',
    cols: ['CONTROL', 'HIDE', 'TINKER'],
    rows: [
      [5, 'Ride a horse through easy terrain.', 'Evade notice under full cover on a moonless night.', 'Open a sticky lock with the appropriate key.'],
      [10, 'Drive an ox-pulled cart.', 'Evade notice in limited cover on a moonless night.', 'Open a simple puzzle box.'],
      [15, 'Ride a horse through rough terrain.', 'Evade notice in limited cover on an average night.', 'Disable a standard trap.'],
      [20, 'Drive a cart through rough terrain.', 'Evade notice in the shadows on an average night.', 'Disable a complicated trap.'],
      [25, 'Ride a wild horse through dangerous terrain.', 'Evade notice with minimal cover in ample light.', 'Open a door secured by a sequence of elaborate locks.'],
      [30, 'Ride an enraged beast through dangerous terrain.', 'Evade notice with no cover in full daylight.', 'Disable an incredibly sensitive and deadly trap.'],
    ],
  },
  {
    trait: 'INSTINCT',
    abbr: 'INS',
    cols: ['PERCEIVE', 'SENSE', 'NAVIGATE'],
    rows: [
      [5, 'Hear a loud noise twenty paces away.', 'Detect an obvious ambush or notice an obvious deception.', 'Follow a well-trod path in good lighting and weather.'],
      [10, 'Hear a speaking voice fifty paces away.', "Detect a looming threat or notice an average person's lies.", 'Follow an average path in good lighting and weather.'],
      [15, 'Hear someone walking in the woods fifty paces away.', "Detect hostile intent from a foe or see through a merchant's lies.", 'Follow a subtle path through rough conditions.'],
      [20, 'Hear someone sneaking through the woods fifty paces away.', "Detect a politician's veiled hostility or detect a nearby assassin.", 'Follow a subtle path through harsh conditions.'],
      [25, 'Hear a prowling animal fifty paces away.', "Identify a spymaster's plot or read a politician's true intentions.", 'Find your way with no path through dangerous conditions.'],
      [30, 'Hear a diving bird a hundred paces away.', "Sense a shred of doubt within a god's pronouncement.", "Find your way through a trickery god's maze."],
    ],
  },
  {
    trait: 'PRESENCE',
    abbr: 'PRE',
    cols: ['CHARM', 'PERFORM', 'DECEIVE'],
    rows: [
      [5, 'Win the trust of a friendly neighbor.', 'Earn a meal from a friendly crowd.', 'Trick a trusting acquaintance.'],
      [10, 'Win the trust of a friendly stranger.', 'Earn room and board in a small town or impress a small crowd.', 'Trick an average stranger.'],
      [15, "Win the trust of a cautious stranger or talk your way into a noble's party.", 'Earn room and board in a low-end tavern or impress a large crowd.', 'Trick an average merchant.'],
      [20, "Win the trust of a sympathetic foe or talk your way into an enemy's party.", 'Earn lodging in a high-end tavern or perform in a full theater.', 'Trick a trained courtier.'],
      [25, 'Turn an enemy against their ruler or talk your way into a fae court.', 'Earn your keep in a royal court or impress a full colosseum.', 'Trick a spymaster.'],
      [30, 'Talk a hostile god into granting you a boon.', 'Save yourself from execution after offending the queen.', 'Trick a god.'],
    ],
  },
  {
    trait: 'KNOWLEDGE',
    abbr: 'KNO',
    cols: ['RECALL', 'ANALYZE', 'COMPREHEND'],
    rows: [
      [5, 'Recall uncommon facts about your community.', 'Unpack an obvious metaphor in a simple text.', 'Learn simple skills from an excellent teacher.'],
      [10, 'Recall uncommon facts about a neighboring community.', 'Identify obvious subtext in a conversation.', 'Learn simple skills from an average teacher.'],
      [15, 'Recall uncommon facts about a distant community.', 'Break an average cipher in a coded message.', 'Learn complicated skills from an excellent teacher.'],
      [20, 'Recall specialized facts about a distant community.', 'Identify a weakness in a complicated battle plan.', 'Learn complicated skills under poor conditions.'],
      [25, 'Recall specialized facts about a fallen kingdom.', 'Predict the downfall of a nation based on concealed misdeeds.', 'Learn complicated skills quickly under dangerous conditions.'],
      [30, 'Recall secret information about an obscure historical group.', "Identify the weakness in a divine champion's fighting form.", 'Learn complicated skills quickly from incomplete instructions.'],
    ],
  },
];

const avgCosts = [
  { item: 'Meals for a party of adventurers per night', cost: '1 Handful' },
  { item: 'Standard inn room per night', cost: '1 Handful' },
  { item: 'Luxury inn room per night', cost: '1 Bag' },
  { item: 'Carriage ride', cost: '2 Handfuls' },
  { item: 'Mount (horse, mule, etc.)', cost: '3 Bags' },
  { item: 'Specialized tools', cost: '3 Handfuls' },
  { item: 'Fine clothing', cost: '3 Handfuls' },
  { item: 'Luxury clothing', cost: '1 Bag' },
  { item: 'Tier 1 equipment (weapons, armor)', cost: '1–5 Handfuls' },
  { item: 'Tier 2 equipment (weapons, armor)', cost: '1–2 Bags' },
  { item: 'Tier 3 equipment (weapons, armor)', cost: '5–10 Bags' },
  { item: 'Tier 4 equipment (weapons, armor)', cost: '1–2 Chests' },
];

export default function GmMechanics() {
  return (
    <>
      <h3 id="rolling-dice">ROLLING DICE</h3>
      <p>The GM has no Duality Dice; instead, they roll a single d20 called the GM's Die.</p>

      <h3 id="adversary-attack-rolls">ADVERSARY ATTACK ROLLS</h3>
      <p>When an adversary attacks a PC, roll your d20 and add the adversary's attack bonus to the result. If the total meets or beats the target's Evasion, the attack succeeds; otherwise, the attack fails. On a successful attack, roll the attack's damage dice to determine how much it deals.</p>
      <p>If you roll a natural 20 on an attack, you automatically succeed and you deal extra damage. Roll damage normally, then add the highest number on the damage dice to the total. For example, an attack that deals 3d6+2 deals 18+3d6+2 on a critical success; the critical success does not affect the flat damage modifier.</p>
      <p className="rule-note"><strong>Note:</strong> a critical success on an adversary's reaction roll automatically succeeds, but confers no additional benefit.</p>

      <h3 id="advantage-disadvantage">GIVING ADVANTAGE AND DISADVANTAGE</h3>
      <p>To viscerally convey how a PC's actions or circumstances affect their ability to act, grant them advantage die (or impose disadvantage die) instead of adjusting the Difficulty of an action roll.</p>

      <h3 id="adversary-action-rolls">ADVERSARY ACTION ROLLS</h3>
      <p>By default, adversaries don't normally make action rolls except for attack rolls and any unique actions described in their stat blocks. Any other action an adversary attempts simply succeeds without an action roll; if you want an adversary's action to have a chance of failure, have any relevant PCs make reaction rolls instead.</p>
      <p>However, for especially dramatic or difficult tasks that the PCs can't influence, you can give an adversary an action roll anyway. For an adversary's action roll, roll a d20. If the result is equal to or greater than the action's Difficulty, the action succeeds — otherwise it fails. You can spend a Fear before rolling to add a relevant Experience from the adversary's stat block to the total. Use the same procedure when an adversary makes a reaction roll.</p>

      <h3 id="adversary-advantage-disadvantage">ADVERSARY ADVANTAGE &amp; DISADVANTAGE</h3>
      <p>If an adversary has advantage on an action roll, the GM rolls an extra d20 and counts only the higher result. If an adversary has disadvantage on an action roll, the GM rolls an extra d20 and counts only the lower result.</p>

      <h3 id="adversary-attacks">ADVERSARY ATTACKS</h3>
      <p>When an adversary attacks a PC, the GM rolls a d20 and adds the adversary's Attack Modifier to the result. If the total meets or beats the target's Evasion, the attack succeeds; otherwise, it fails. Before rolling, the GM can grant the attacking adversary advantage, impose disadvantage, or spend a Fear to add a relevant Experience from the adversary's stat block to the total.</p>
      <p>On a success, the adversary deals the damage listed in their stat block to the target.</p>
      <p>When an adversary's action lets the GM make an attack against multiple targets, they make one attack roll and compare it to each target's Evasion separately.</p>

      <h3 id="difficulty-benchmarks">DIFFICULTY BENCHMARKS</h3>
      <p>The Difficulty of an attack roll against an adversary is equal to the adversary's Difficulty score. The Difficulty of any other action rolls against an adversary is equal to the adversary's Difficulty score, plus (if applicable) the value of one of the adversary relevant Experience modifiers.</p>
      <p>When a player makes an action roll without a specified Difficulty, the GM sets the Difficulty according to the totality of the circumstances. Refer to the following benchmark tables for guidance:</p>

      {traitTables.map((t) => (
        <div key={t.trait} style={{ marginBottom: '24px', overflowX: 'auto' }}>
          <table className="equipment-table benchmark-table">
            <thead>
              <tr>
                <th className="benchmark-roll-col">
                  <span className="trait-label-full">{t.trait} ROLL</span>
                  <span className="trait-label-short">{t.abbr}</span>
                </th>
                <th>{t.cols[0]}</th>
                <th>{t.cols[1]}</th>
                <th>{t.cols[2]}</th>
              </tr>
            </thead>
            <tbody>
              {t.rows.map(([roll, c1, c2, c3]) => (
                <tr key={roll}>
                  <td className="benchmark-roll-col" style={{ textAlign: 'center' }}><strong>{roll}</strong></td>
                  <td style={{ fontSize: '13px' }}>{c1}</td>
                  <td style={{ fontSize: '13px' }}>{c2}</td>
                  <td style={{ fontSize: '13px' }}>{c3}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}

      <h3 id="countdowns">COUNTDOWNS</h3>
      <p>Countdowns represent a period of time or series of events preceding a future effect. A countdown begins at a starting value. When a countdown <strong>advances</strong>, it's reduced by 1. The countdown's effect is triggered when the countdown reaches 0.</p>
      <p className="rule-note"><strong>Note:</strong> You can track countdowns by "spinning down" dice or ticking off boxes.</p>
      <p><strong>Standard countdowns</strong> advance every time a player makes an action roll. If an adversary or environment ability refers to a "Countdown [n]," it means a standard countdown with a starting value of n.</p>
      <p><strong>Dynamic countdowns</strong> advance by up to 3 depending on the outcomes of action rolls. <strong>Consequence countdowns</strong> are dynamic countdowns to negative effects. <strong>Progress countdowns</strong> are dynamic countdowns to positive effects. Dynamic countdowns advance according to this chart:</p>

      <table className="equipment-table" style={{ marginBottom: '16px', maxWidth: '480px' }}>
        <thead>
          <tr>
            <th>Roll Result</th>
            <th>Progress Advancement</th>
            <th>Consequence Advancement</th>
          </tr>
        </thead>
        <tbody>
          <tr><td>Failure with Fear</td><td>No advancement</td><td>Tick down 3</td></tr>
          <tr><td>Failure with Hope</td><td>No advancement</td><td>Tick down 2</td></tr>
          <tr><td>Success with Fear</td><td>Tick down 1</td><td>Tick down 1</td></tr>
          <tr><td>Success with Hope</td><td>Tick down 2</td><td>No advancement</td></tr>
          <tr><td>Critical Success</td><td>Tick down 3</td><td>No advancement</td></tr>
        </tbody>
      </table>

      <h3 id="advanced-countdown-features">ADVANCED COUNTDOWN FEATURES</h3>
      <ul>
        <li>Countdowns with <strong>randomized starting values</strong></li>
        <li><strong>Loop</strong> countdowns that reset to their starting value after their countdown effect is triggered.</li>
        <li><strong>Increasing</strong> countdowns that increase their starting value by 1 every time they loop.</li>
        <li><strong>Decreasing</strong> countdowns that decrease their starting value by 1 every time they loop.</li>
        <li><strong>Linked</strong> progress and consequence countdowns that simultaneously advance according to the same action roll outcomes.</li>
        <li><strong>Long-term</strong> countdowns that advance after rests instead of action rolls.</li>
      </ul>

      <h3 id="gold-loot">GIVING OUT GOLD, EQUIPMENT, AND LOOT</h3>
      <p>It's up to you and your players how much importance you want to place on gold, equipment, and loot in your campaign.</p>
      <p>Adjust the availability and utility of wealth and equipment to reflect the tone, themes, and setting of your campaign.</p>
      <p>If you don't wish to track gold, then when PCs go shopping for new items let them pick one or two from a short, preselected list that aligns with your campaign setting and the party's current location.</p>
      <p>Otherwise, set the prices of goods and services by adjusting the entries in the Average Costs table to reflect your campaign setting:</p>

      <table className="equipment-table" style={{ marginBottom: '24px', maxWidth: '420px' }}>
        <thead>
          <tr>
            <th>Item</th>
            <th>Average Cost</th>
          </tr>
        </thead>
        <tbody>
          {avgCosts.map((c) => (
            <tr key={c.item}>
              <td style={{ fontSize: '13px' }}>{c.item}</td>
              <td style={{ whiteSpace: 'nowrap' }}>{c.cost}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <h3 id="running-npcs">RUNNING GM NPCS</h3>
      <p>When you run NPCs as the GM, you should always strive to follow your GM principles and use them to bring the world to life. Differentiate NPCs with unique manners of speech and action; let their individual goals and desires motivate their actions.</p>
      <p>The only essential elements for a NPC are their <strong>name</strong>, <strong>description</strong>, and <strong>motive</strong>. If it's likely that the PCs will roll actions against them, give them a <strong>Difficulty</strong>. Adversaries can be invented or improvised by modifying the stat block of another adversary.</p>
      <p>If an NPC becomes an ally in combat, they don't need a stat block — just put the spotlight on what they do and show how their involvement alters the fiction. If a PC capitalizes on their help during the scene, give the PC advantage. NPCs that don't have Hit Points or Stress can still be injured or killed if the fiction demands it.</p>
      <p>If you want an important NPC to mechanically interact with the system, you can give them one or more features with specific <strong>triggers</strong> and <strong>effects</strong>. An NPC might also have a choice that adjusts the parameters of their feature. For example:</p>
      <div className="feature-block">
        <div className="feature-name">ARCANE HOLD</div>
        <div className="feature-text">
          <p><em>Choice:</em> When the battle begins, choose a favored PC.</p>
          <p><em>Trigger:</em> The first time during a battle the favored PC is within Close range and hit with an attack.</p>
          <p><em>Effect:</em> Make an attack roll with a +6 modifier against the adversary. On a success, the target is temporarily Restrained by tendrils of powerful magic.</p>
        </div>
      </div>
    </>
  );
}
